import { useEffect, useState, type FormEvent } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, deleteDoc, serverTimestamp, type Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth-context';

interface TrainingDoc {
  id: string;
  title: string;
  body: string;
  category: 'getting-started' | 'standards' | 'patterns' | 'examples' | 'reference';
  createdByName?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

const CATEGORY_LABELS: Record<TrainingDoc['category'], string> = {
  'getting-started': 'Getting started',
  standards: 'QC standards',
  patterns: 'Patterns to spot',
  examples: 'Examples',
  reference: 'Reference'
};

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS) as [TrainingDoc['category'], string][];

function isHtml(body: string): boolean {
  // crude but adequate: anything that has a tag-shaped substring
  return /<\w+[\s>]/.test(body);
}

export default function TrainingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [doc_, setDoc] = useState<TrainingDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState<TrainingDoc['category']>('getting-started');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(doc(db, 'training', id), (snap) => {
      if (snap.exists()) {
        setDoc({ id: snap.id, ...snap.data() } as TrainingDoc);
      }
      setLoading(false);
    });
    return unsub;
  }, [id]);

  if (loading) return <div className="empty">Loading…</div>;
  if (!doc_) return <div className="empty">Training doc not found.</div>;

  function startEdit() {
    if (!doc_) return;
    setEditTitle(doc_.title);
    setEditCategory(doc_.category);
    setEditBody(doc_.body);
    setEditing(true);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'training', id), {
        title: editTitle.trim(),
        category: editCategory,
        body: editBody,
        updatedAt: serverTimestamp()
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!confirm(`Delete training doc "${doc_!.title}"? This can't be undone.`)) return;
    await deleteDoc(doc(db, 'training', id));
    navigate('/training');
  }

  return (
    <div className="page page--training-detail">
      <div className="page__breadcrumb">
        <Link to="/training" className="breadcrumb">← All training</Link>
      </div>

      <div className="page__header">
        <div>
          <div className="page__tags">
            <span className="tag tag--ghost">{CATEGORY_LABELS[doc_.category] || doc_.category}</span>
          </div>
          <h1 className="page__title">{doc_.title}</h1>
          {doc_.createdByName && (
            <p className="page__subtitle">by {doc_.createdByName}</p>
          )}
        </div>
        {profile?.role === 'admin' && !editing && (
          <div className="page__actions">
            <button className="btn btn--ghost" onClick={startEdit}>✏️ Edit</button>
            <button className="btn btn--ghost btn--danger" onClick={handleDelete}>🗑 Delete</button>
          </div>
        )}
      </div>

      {editing ? (
        <form className="training-edit" onSubmit={handleSave}>
          <label className="field">
            <span className="field__label">Title</span>
            <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Category</span>
            <select value={editCategory} onChange={(e) => setEditCategory(e.target.value as TrainingDoc['category'])}>
              {CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Body (plain text or HTML)</span>
            <textarea rows={18} value={editBody} onChange={(e) => setEditBody(e.target.value)} required />
          </label>
          <div className="training-edit__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={saving || !editTitle.trim() || !editBody.trim()}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      ) : isHtml(doc_.body) ? (
        <div
          className="training-detail__html"
          dangerouslySetInnerHTML={{ __html: doc_.body }}
        />
      ) : (
        <div className="training-detail__text">{doc_.body}</div>
      )}
    </div>
  );
}
